import React, { useState, useEffect, useMemo } from 'react';
import {
  TrendingUpIcon,
  ActivityIcon,
  CrownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PlayIcon,
  PauseIcon,
  RotateCcwIcon,
  SparklesIcon,
  LayersIcon,
  GitBranchIcon
} from 'lucide-react';
import { resolveFriendlyName } from '../shared/modelMap';

const EvolutionView = ({
  rounds,
  universes,
  initialBattle,
  tournamentWinner,
  multiverseWinner,
  eliminationOrder,
  mode = 'tournament' // 'tournament' or 'multiverse'
}) => {
  const [currentRound, setCurrentRound] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [animationSpeed, setAnimationSpeed] = useState(1500);
  const [selectedUniverse, setSelectedUniverse] = useState(0);
  const [showAllUniverses, setShowAllUniverses] = useState(false);

  // Prepare data based on mode
  const evolutionData = useMemo(() => {
    if (mode === 'multiverse' && universes && initialBattle) {
      // For multiverse mode, combine initial battle with universe rounds
      const allRoundsData = [];

      // Initial battle as round 0
      allRoundsData.push({
        round: 0,
        type: 'initial',
        label: 'Initial Battle',
        scores: {
          gpt: initialBattle.scores?.gpt || 0,
          claude: initialBattle.scores?.claude || 0,
          gemini: initialBattle.scores?.gemini || 0
        },
        eliminated: null,
        activeModels: ['gpt', 'claude', 'gemini']
      });

      // Universe-specific rounds
      if (universes[selectedUniverse]) {
        const universe = universes[selectedUniverse];
        universe.rounds?.forEach((r, idx) => {
          allRoundsData.push({
            round: idx + 1,
            type: r.type,
            label: r.type === 'final' ? 'Championship' : `Round ${idx + 2}`,
            scores: {
              gpt: r.scores?.gpt || 0,
              claude: r.scores?.claude || 0,
              gemini: r.scores?.gemini || 0
            },
            eliminated: r.eliminated,
            activeModels: ['gpt', 'claude', 'gemini'].filter(m => r.scores?.[m] > 0)
          });
        });
      }

      return allRoundsData;
    } else if (rounds) {
      // Tournament mode
      return rounds.map((r, idx) => ({
        round: idx,
        type: r.type,
        label: r.type === 'initial' ? 'Initial Battle' :
               r.type === 'final' ? 'Championship' : `Round ${idx + 1}`,
        scores: {
          gpt: r.scores?.gpt || 0,
          claude: r.scores?.claude || 0,
          gemini: r.scores?.gemini || 0
        },
        eliminated: r.eliminated,
        activeModels: ['gpt', 'claude', 'gemini'].filter(m =>
          r.responses?.[m]?.success && !r.responses?.[m]?.disqualified
        )
      }));
    }
    return [];
  }, [rounds, universes, initialBattle, selectedUniverse, mode]);

  // Calculate max score for normalization
  const maxScore = useMemo(() => {
    let max = 0;
    evolutionData.forEach(r => {
      Object.values(r.scores).forEach(s => {
        if (s > max) max = s;
      });
    });
    return Math.max(max, 100);
  }, [evolutionData]);

  // Animation effect
  useEffect(() => {
    if (!isAnimating) return;

    const interval = setInterval(() => {
      setCurrentRound(prev => {
        if (prev >= evolutionData.length - 1) {
          setIsAnimating(false);
          return prev;
        }
        return prev + 1;
      });
    }, animationSpeed);

    return () => clearInterval(interval);
  }, [isAnimating, evolutionData.length, animationSpeed]);

  // Reset animation when universe changes
  useEffect(() => {
    setCurrentRound(0);
    setIsAnimating(false);
  }, [selectedUniverse]);

  const getModelColor = (model) => {
    const key = model?.toLowerCase();
    if (key?.includes('gpt')) return { main: '#10b981', light: 'rgba(16, 185, 129, 0.2)', name: 'emerald' };
    if (key?.includes('claude')) return { main: '#3b82f6', light: 'rgba(59, 130, 246, 0.2)', name: 'blue' };
    if (key?.includes('gemini')) return { main: '#a855f7', light: 'rgba(168, 85, 247, 0.2)', name: 'purple' };
    return { main: '#64748b', light: 'rgba(100, 116, 139, 0.2)', name: 'slate' };
  };

  const getColorClasses = (colorName) => {
    const classes = {
      emerald: { bg: 'bg-emerald-500', text: 'text-emerald-400', border: 'border-emerald-500/30', glow: 'shadow-emerald-500/20' },
      blue: { bg: 'bg-blue-500', text: 'text-blue-400', border: 'border-blue-500/30', glow: 'shadow-blue-500/20' },
      purple: { bg: 'bg-purple-500', text: 'text-purple-400', border: 'border-purple-500/30', glow: 'shadow-purple-500/20' },
      slate: { bg: 'bg-slate-500', text: 'text-slate-400', border: 'border-slate-500/30', glow: 'shadow-slate-500/20' }
    };
    return classes[colorName] || classes.slate;
  };

  const handlePlayPause = () => {
    if (currentRound >= evolutionData.length - 1) {
      setCurrentRound(0);
    }
    setIsAnimating(!isAnimating);
  };

  const handleReset = () => {
    setCurrentRound(0);
    setIsAnimating(false);
  };

  const handlePrev = () => {
    setIsAnimating(false);
    setCurrentRound(Math.max(0, currentRound - 1));
  };

  const handleNext = () => {
    setIsAnimating(false);
    setCurrentRound(Math.min(evolutionData.length - 1, currentRound + 1));
  };

  // Calculate deltas from previous round
  const getDeltas = (roundIdx) => {
    if (roundIdx === 0) return { gpt: 0, claude: 0, gemini: 0 };
    const current = evolutionData[roundIdx]?.scores || {};
    const prev = evolutionData[roundIdx - 1]?.scores || {};
    return {
      gpt: (current.gpt || 0) - (prev.gpt || 0),
      claude: (current.claude || 0) - (prev.claude || 0),
      gemini: (current.gemini || 0) - (prev.gemini || 0)
    };
  };

  const currentData = evolutionData[currentRound] || evolutionData[0];
  const deltas = getDeltas(currentRound);
  const winner = mode === 'multiverse' ? multiverseWinner : tournamentWinner;

  // Calculate overall evolution stats
  const evolutionStats = useMemo(() => {
    if (evolutionData.length < 2) return null;

    const first = evolutionData[0]?.scores || {};
    const last = evolutionData[evolutionData.length - 1]?.scores || {};

    const stats = {};
    ['gpt', 'claude', 'gemini'].forEach(m => {
      const initial = first[m] || 0;
      const final = last[m] || 0;
      const change = final - initial;
      const percentChange = initial > 0 ? Math.round((change / initial) * 100) : 0;
      stats[m] = { initial, final, change, percentChange };
    });

    return stats;
  }, [evolutionData]);

  if (!evolutionData.length) return null;

  return (
    <div className="glass border border-slate-700/50 rounded-2xl p-6 shadow-xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-xl bg-gradient-to-br from-cyan-500/20 to-purple-500/20 border border-cyan-500/30">
            <ActivityIcon className="h-6 w-6 text-cyan-400" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-white">Evolution View</h3>
            <p className="text-sm text-slate-400">Track score progression through rounds</p>
          </div>
        </div>

        {/* Universe Selector (Multiverse mode only) */}
        {mode === 'multiverse' && universes && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-400 mr-2">Timeline:</span>
            <div className="flex rounded-lg overflow-hidden border border-slate-600/50">
              {universes.map((u, idx) => (
                <button
                  key={idx}
                  onClick={() => setSelectedUniverse(idx)}
                  className={`px-3 py-1.5 text-xs font-medium transition-all ${
                    selectedUniverse === idx
                      ? 'bg-purple-500/30 text-purple-300 border-purple-500/50'
                      : 'bg-slate-800/50 text-slate-400 hover:bg-slate-700/50'
                  }`}
                >
                  {u.name.replace('Universe ', 'U')}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Main Visualization Area */}
      <div className="relative">
        {/* Simplified Bar Chart Visualization */}
        <div className="mb-6 bg-slate-900/50 rounded-xl border border-slate-700/30 p-6">
          {/* Round Label */}
          <div className="text-center mb-4">
            <span className="text-sm text-slate-400">
              {currentData?.label || `Round ${currentRound + 1}`}
            </span>
            {currentData?.eliminated && (
              <span className="ml-3 text-xs text-red-400 bg-red-500/20 px-2 py-0.5 rounded-full">
                💀 {resolveFriendlyName(currentData.eliminated)} eliminated
              </span>
            )}
          </div>

          {/* Horizontal Bar Chart - Much clearer visualization */}
          <div className="space-y-4">
            {['gpt', 'claude', 'gemini'].map(model => {
              const color = getModelColor(model);
              const colorClasses = getColorClasses(color.name);
              const score = currentData?.scores?.[model] || 0;
              const isActive = currentData?.activeModels?.includes(model);
              const isEliminated = currentData?.eliminated === model;
              const isWinner = model === winner?.toLowerCase();
              const percentage = (score / maxScore) * 100;
              const delta = deltas[model];

              return (
                <div
                  key={model}
                  className={`transition-all duration-500 ${!isActive && !isEliminated ? 'opacity-30' : ''}`}
                >
                  {/* Model Name & Score */}
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full ${colorClasses.bg} ${isEliminated ? 'bg-red-500' : ''}`} />
                      <span className={`font-medium text-sm ${isEliminated ? 'text-red-400 line-through' : 'text-white'}`}>
                        {resolveFriendlyName(model)}
                      </span>
                      {isWinner && isActive && (
                        <CrownIcon className="h-4 w-4 text-yellow-400" />
                      )}
                      {isEliminated && (
                        <span className="text-red-400 text-xs">💀 OUT</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Delta indicator */}
                      {isActive && delta !== 0 && (
                        <span className={`text-xs font-medium ${delta > 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {delta > 0 ? '↑' : '↓'}{Math.abs(Math.round(delta))}
                        </span>
                      )}
                      <span className={`font-bold text-lg ${isEliminated ? 'text-red-400' : colorClasses.text}`}>
                        {Math.round(score)}
                      </span>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="relative h-6 bg-slate-800/80 rounded-lg overflow-hidden">
                    <div
                      className={`absolute inset-y-0 left-0 rounded-lg transition-all duration-700 ease-out ${
                        isEliminated ? 'bg-red-500/60' : ''
                      }`}
                      style={{
                        width: `${Math.min(percentage, 100)}%`,
                        background: isEliminated ? undefined : `linear-gradient(90deg, ${color.main}aa, ${color.main})`
                      }}
                    />
                    {/* Score inside bar if wide enough */}
                    {percentage > 15 && (
                      <span className="absolute inset-y-0 left-3 flex items-center text-white/90 font-semibold text-sm">
                        {Math.round(score)} pts
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Score Scale Reference */}
          <div className="flex justify-between mt-4 text-xs text-slate-500 border-t border-slate-700/30 pt-3">
            <span>0</span>
            <span>Score Scale</span>
            <span>{Math.round(maxScore)}</span>
          </div>
        </div>

        {/* Playback Controls */}
        <div className="flex items-center justify-center gap-4 mb-6">
          <button
            onClick={handleReset}
            className="p-2 glass rounded-lg hover:bg-white/10 transition-all text-slate-400 hover:text-white"
            title="Reset"
          >
            <RotateCcwIcon className="h-5 w-5" />
          </button>

          <button
            onClick={handlePrev}
            disabled={currentRound === 0}
            className="p-2 glass rounded-lg hover:bg-white/10 transition-all text-slate-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
            title="Previous round"
          >
            <ChevronLeftIcon className="h-5 w-5" />
          </button>

          <button
            onClick={handlePlayPause}
            className={`p-3 rounded-xl transition-all ${
              isAnimating
                ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                : 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
            } hover:scale-105`}
            title={isAnimating ? 'Pause' : 'Play animation'}
          >
            {isAnimating ? (
              <PauseIcon className="h-6 w-6" />
            ) : (
              <PlayIcon className="h-6 w-6" />
            )}
          </button>

          <button
            onClick={handleNext}
            disabled={currentRound >= evolutionData.length - 1}
            className="p-2 glass rounded-lg hover:bg-white/10 transition-all text-slate-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
            title="Next round"
          >
            <ChevronRightIcon className="h-5 w-5" />
          </button>

          {/* Speed selector */}
          <div className="ml-4 flex items-center gap-2">
            <span className="text-xs text-slate-500">Speed:</span>
            <select
              value={animationSpeed}
              onChange={(e) => setAnimationSpeed(Number(e.target.value))}
              className="bg-slate-800 border border-slate-600/50 rounded-lg px-2 py-1 text-xs text-slate-300 focus:outline-none focus:ring-1 focus:ring-cyan-500"
            >
              <option value={2500}>Slow</option>
              <option value={1500}>Normal</option>
              <option value={800}>Fast</option>
              <option value={400}>Instant</option>
            </select>
          </div>
        </div>

        {/* Round Progress Bar */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-slate-400">Round Progress</span>
            <span className="text-xs text-slate-400">
              {currentData?.label || `Round ${currentRound + 1}`}
            </span>
          </div>
          <div className="relative">
            <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-cyan-500 to-purple-500 transition-all duration-500"
                style={{ width: `${((currentRound + 1) / evolutionData.length) * 100}%` }}
              />
            </div>
            {/* Round dots */}
            <div className="absolute inset-0 flex items-center justify-between px-0">
              {evolutionData.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    setIsAnimating(false);
                    setCurrentRound(idx);
                  }}
                  className={`w-4 h-4 rounded-full border-2 transition-all -mt-0.5 ${
                    idx <= currentRound
                      ? 'bg-cyan-500 border-cyan-400 shadow-lg shadow-cyan-500/50'
                      : 'bg-slate-700 border-slate-600 hover:border-slate-500'
                  }`}
                  title={evolutionData[idx]?.label}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Overall Evolution Stats (at the end) */}
        {evolutionStats && currentRound === evolutionData.length - 1 && (
          <div className="border-t border-slate-700/50 pt-6">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUpIcon className="h-5 w-5 text-cyan-400" />
              <h4 className="font-semibold text-white">Total Evolution Summary</h4>
            </div>

            <div className="grid grid-cols-3 gap-4">
              {['gpt', 'claude', 'gemini'].map(model => {
                const stats = evolutionStats[model];
                const color = getModelColor(model);
                const colorClasses = getColorClasses(color.name);
                const isWinner = model === winner?.toLowerCase();

                return (
                  <div
                    key={model}
                    className={`glass rounded-xl p-4 border ${
                      isWinner ? `${colorClasses.border} ring-1 ring-yellow-500/20` : 'border-slate-700/30'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <div className={`w-2 h-2 rounded-full ${colorClasses.bg}`} />
                      <span className="font-medium text-white text-sm">
                        {resolveFriendlyName(model)}
                      </span>
                      {isWinner && <CrownIcon className="h-4 w-4 text-yellow-400 ml-auto" />}
                    </div>

                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-400">Initial:</span>
                        <span className="text-slate-300">{Math.round(stats.initial)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Final:</span>
                        <span className={colorClasses.text}>{Math.round(stats.final)}</span>
                      </div>
                      <div className="flex justify-between border-t border-slate-700/50 pt-2 mt-2">
                        <span className="text-slate-400">Change:</span>
                        <span className={`font-semibold ${
                          stats.change > 0 ? 'text-green-400' :
                          stats.change < 0 ? 'text-red-400' : 'text-slate-400'
                        }`}>
                          {stats.change > 0 ? '+' : ''}{Math.round(stats.change)}
                          {stats.percentChange !== 0 && (
                            <span className="text-xs ml-1">
                              ({stats.percentChange > 0 ? '+' : ''}{stats.percentChange}%)
                            </span>
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Insight */}
            <div className="mt-4 p-4 rounded-xl bg-gradient-to-r from-cyan-500/10 to-purple-500/10 border border-cyan-500/20">
              <div className="flex items-start gap-3">
                <SparklesIcon className="h-5 w-5 text-cyan-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-white font-medium">
                    {(() => {
                      const winnerStats = evolutionStats[winner?.toLowerCase()];
                      if (!winnerStats) return 'Tournament complete!';

                      if (winnerStats.change > 10) {
                        return `${resolveFriendlyName(winner?.toLowerCase())} showed remarkable improvement, climbing ${Math.round(winnerStats.change)} points through adversarial refinement.`;
                      } else if (winnerStats.change > 0) {
                        return `${resolveFriendlyName(winner?.toLowerCase())} maintained strong performance and improved steadily throughout the tournament.`;
                      } else if (winnerStats.change < -10) {
                        return `Despite score fluctuations, ${resolveFriendlyName(winner?.toLowerCase())} emerged victorious through strategic adaptation.`;
                      } else {
                        return `${resolveFriendlyName(winner?.toLowerCase())} demonstrated consistent excellence across all rounds.`;
                      }
                    })()}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Multiverse Comparison Toggle */}
      {mode === 'multiverse' && universes && (
        <div className="mt-6 border-t border-slate-700/50 pt-6">
          <button
            onClick={() => setShowAllUniverses(!showAllUniverses)}
            className="flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-all mb-4"
          >
            <LayersIcon className="h-4 w-4" />
            {showAllUniverses ? 'Hide' : 'Show'} All Universe Comparisons
            <ChevronRightIcon className={`h-4 w-4 transition-transform ${showAllUniverses ? 'rotate-90' : ''}`} />
          </button>

          {showAllUniverses && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {universes.map((u, idx) => {
                const universeWinner = u.winner?.toLowerCase();
                const winnerColor = getModelColor(universeWinner);
                const winnerClasses = getColorClasses(winnerColor.name);

                return (
                  <div
                    key={idx}
                    className={`glass rounded-xl p-4 border cursor-pointer transition-all ${
                      selectedUniverse === idx
                        ? `${winnerClasses.border} ring-1 ${winnerClasses.glow}`
                        : 'border-slate-700/50 hover:border-slate-600/50'
                    }`}
                    onClick={() => setSelectedUniverse(idx)}
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <GitBranchIcon className={`h-4 w-4 ${selectedUniverse === idx ? winnerClasses.text : 'text-slate-400'}`} />
                      <span className="font-medium text-white">{u.name}</span>
                    </div>

                    <div className="flex items-center justify-between text-sm mb-2">
                      <span className="text-slate-400">Path:</span>
                      <span className="text-slate-300">
                        {resolveFriendlyName(u.firstEliminated).split(' ')[0]} →
                        {resolveFriendlyName(u.secondEliminated).split(' ')[0]} →
                        <span className={winnerClasses.text}>{resolveFriendlyName(universeWinner).split(' ')[0]}</span>
                      </span>
                    </div>

                    <div className="flex items-center justify-between">
                      <span className={`font-semibold ${winnerClasses.text}`}>
                        Winner: {resolveFriendlyName(universeWinner)}
                      </span>
                      <span className="text-slate-400 text-sm">{u.finalScore} pts</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EvolutionView;
